/**
 * STARS — seeded night star field: crisp round dots, small four-point sparkles and a few big
 * golden sparkles (no blur, no glow), drifting slowly on the hour clock. The big ones (and a
 * few small ones) twinkle on the wobble clock.
 *
 * `layoutStarField` is the single placement both renderers use. Canvas 2D bakes the steady
 * stars into a tile once and scrolls it, then draws the twinklers live; the Pixi sky layer
 * places one sprite per star (render/pixi/sky-shader.js).
 */

import { normalizeTimeOfDay, timeSec } from "./sky-utils.js";
import { combineSkyDecorSeed, seededHash01 } from "../../core/day-night.js";
import { sparklePath } from "../toon/toon-kit.js";
import { getStarCanvas, STAR_SPRITE_FILL } from "../toon/sky-art.js";

export var STAR_COUNT = 140;
/** Horizontal drift speed (CSS px per anim-second of the hour clock). */
export var STAR_DRIFT_PX_PER_SEC = 1.5;
/** Baked tile resolution cap: 2 keeps the sparkle points crisp on HiDPI screens. */
export var STAR_TILE_MAX_DPR = 2;

var DOT_RGB = [222, 234, 255];
var SPARKLE_RGB = [255, 247, 214];
var BIG_RGB = [255, 232, 150];

var _starTile = {
  key: null,
  canvas: null,
  ctx: null,
  pixelW: 0,
  pixelH: 0,
};

var _layout = { key: null, stars: null };

/** Night star visibility: fade in dusk→night, out near dawn. */
export function starFieldAlpha(timeOfDay) {
  var t = normalizeTimeOfDay(timeOfDay);
  if (t <= 0.55) return 0;
  if (t < 0.7) return (t - 0.55) / 0.15;
  if (t <= 0.95) return 1;
  if (t < 1) return Math.max(0, 1 - (t - 0.95) / 0.05);
  return 0;
}

export function resetStarFieldCache() {
  _starTile.key = null;
  _starTile.canvas = null;
  _starTile.ctx = null;
  _starTile.pixelW = 0;
  _starTile.pixelH = 0;
  _layout.key = null;
  _layout.stars = null;
}

/** Star size scale for a sky band width. */
export function starScale(width) {
  return Math.max(0.6, Math.min(1.5, (Number(width) || 0) / 720));
}

/**
 * Seeded star placement for a sky band of w × h (positions before drift).
 * @returns {{ x: number, y: number, r: number, kind: "dot"|"sparkle", rgb: number[],
 *   alpha: number, twinkle: boolean, phase: number, rate: number }[]}
 */
export function layoutStarField(w, h, decorSeed) {
  var key = w + "x" + h + "|" + String(decorSeed);
  if (_layout.key === key && _layout.stars) return _layout.stars;
  var scale = starScale(w);
  var stars = [];
  for (var i = 0; i < STAR_COUNT; i++) {
    var s = decorSeed != null ? combineSkyDecorSeed(decorSeed, i + 101) : i + 101;
    var hx = seededHash01(s, 3.17);
    var hy = seededHash01(s, 8.41);
    var hs = seededHash01(s, 5.93);
    var hp = seededHash01(s, 1.27);
    var hroll = seededHash01(s, 4.62);
    // 40% of the stars crowd the lower sky, so the horizon twinkles under the moon.
    var ty = i < STAR_COUNT * 0.4 ? (0.6 + hy * 0.36) : hy * 0.96;
    var star = {
      x: hx * w,
      y: ty * h,
      r: 0,
      kind: "dot",
      rgb: DOT_RGB,
      alpha: 0.5 + 0.45 * hs,
      twinkle: false,
      phase: hp * Math.PI * 2,
      rate: 1.6 + hs * 1.8,
    };
    if (hroll < 0.58) {
      star.r = (0.75 + 0.85 * hs) * scale;
    } else if (hroll < 0.91) {
      star.kind = "sparkle";
      star.rgb = SPARKLE_RGB;
      star.r = (2.3 + 1.7 * hs) * scale;
      star.alpha = 0.78 + 0.22 * hs;
      star.twinkle = hs > 0.72;
    } else {
      star.kind = "sparkle";
      star.rgb = BIG_RGB;
      star.r = (4.4 + 2.8 * hs) * scale;
      star.alpha = 1;
      star.twinkle = true;
    }
    stars.push(star);
  }
  _layout.key = key;
  _layout.stars = stars;
  return stars;
}

/**
 * Twinkle of one star at a moment of the wobble clock: scale and alpha multipliers.
 * Twinklers pulse between a small glint and full size; the rest hold still.
 */
export function starTwinkle(star, wobbleSec) {
  if (!star.twinkle) return { scale: 1, alpha: 1 };
  var v = 0.5 + 0.5 * Math.sin((Number(wobbleSec) || 0) * star.rate + star.phase);
  var k = v * v;
  return { scale: 0.5 + 0.62 * k, alpha: 0.55 + 0.45 * k };
}

function starTileKey(w, h, decorSeed, bakeDpr) {
  return w + "x" + h + "|" + String(decorSeed) + "|dpr" + bakeDpr;
}

/** @internal Test hook — current baked tile cache key, or null. */
export function getStarTileCacheKeyForTests() {
  return _starTile.key;
}

function paintStar(c, star, x, y, scale, alpha) {
  var r = star.r * scale;
  if (!(r > 0.2) || alpha <= 0.003) return;
  c.fillStyle = "rgba(" + star.rgb[0] + "," + star.rgb[1] + "," + star.rgb[2] + "," + alpha.toFixed(3) + ")";
  c.beginPath();
  if (star.kind === "sparkle" && typeof c.quadraticCurveTo === "function") {
    sparklePath(c, x, y, r, 0);
    c.fill();
    c.beginPath();
    c.arc(x, y, r * 0.2, 0, Math.PI * 2);
  } else {
    c.arc(x, y, star.kind === "sparkle" ? r * 0.35 : r, 0, Math.PI * 2);
  }
  c.fill();
}

function paintStarField(tileCtx, w, h, decorSeed) {
  tileCtx.clearRect(0, 0, w, h);
  var stars = layoutStarField(w, h, decorSeed);
  for (var i = 0; i < stars.length; i++) {
    var st = stars[i];
    if (st.twinkle) continue;
    paintStar(tileCtx, st, st.x, st.y, 1, st.alpha);
  }
}

function ensureStarTile(w, h, decorSeed, dpr) {
  var safeDpr = dpr > 0 ? dpr : 1;
  var bakeDpr = Math.min(safeDpr, STAR_TILE_MAX_DPR > 0 ? STAR_TILE_MAX_DPR : 1);
  var key = starTileKey(w, h, decorSeed, bakeDpr);
  if (_starTile.key === key && _starTile.canvas && _starTile.ctx) {
    return _starTile;
  }
  if (typeof document === "undefined") return null;
  var pixelW = Math.max(1, Math.round(w * bakeDpr));
  var pixelH = Math.max(1, Math.round(h * bakeDpr));
  if (!_starTile.canvas) {
    _starTile.canvas = document.createElement("canvas");
    _starTile.ctx = _starTile.canvas.getContext("2d");
  }
  var canvas = _starTile.canvas;
  var tileCtx = _starTile.ctx;
  if (!tileCtx) return null;
  if (canvas.width !== pixelW || canvas.height !== pixelH) {
    canvas.width = pixelW;
    canvas.height = pixelH;
  }
  tileCtx.setTransform(bakeDpr, 0, 0, bakeDpr, 0, 0);
  paintStarField(tileCtx, w, h, decorSeed);
  _starTile.key = key;
  _starTile.pixelW = pixelW;
  _starTile.pixelH = pixelH;
  return _starTile;
}

/**
 * Baked tile of the steady stars for a sky band of width × height (Canvas 2D path).
 * Null when the band is empty or there is no document.
 */
export function getStarFieldTile(width, height, decorSeed, dpr) {
  var w = Math.max(0, Number(width) || 0);
  var h = Math.max(0, Number(height) || 0);
  if (w <= 0 || h <= 0) return null;
  return ensureStarTile(w, h, decorSeed, dpr);
}

function blitScrollingStarTile(ctx, tile, bounds, driftPx, fieldAlpha) {
  var x = bounds.x;
  var y = bounds.y;
  var w = bounds.width;
  var h = bounds.height;
  var off = ((driftPx % w) + w) % w;
  var prevAlpha = ctx.globalAlpha;
  ctx.globalAlpha = prevAlpha * fieldAlpha;
  ctx.drawImage(tile.canvas, 0, 0, tile.pixelW, tile.pixelH, x - off, y, w, h);
  ctx.drawImage(tile.canvas, 0, 0, tile.pixelW, tile.pixelH, x - off + w, y, w, h);
  ctx.globalAlpha = prevAlpha;
}

/** Star x after the drift, wrapped into the band (drift moves the field left). */
export function starDriftX(star, w, driftPx) {
  return ((star.x - driftPx) % w + w) % w;
}

function drawTwinklers(ctx, bounds, stars, driftPx, fieldAlpha, wobbleSec) {
  if (typeof ctx.drawImage !== "function") return;
  var prevAlpha = ctx.globalAlpha;
  for (var i = 0; i < stars.length; i++) {
    var st = stars[i];
    if (!st.twinkle) continue;
    var sprite = getStarCanvas("sparkle", st.rgb);
    if (!sprite) return;
    var tw = starTwinkle(st, wobbleSec);
    var r = st.r * tw.scale / STAR_SPRITE_FILL;
    var sx = bounds.x + starDriftX(st, bounds.width, driftPx);
    var sy = bounds.y + st.y;
    ctx.globalAlpha = prevAlpha * fieldAlpha * tw.alpha * st.alpha;
    ctx.drawImage(sprite, sx - r, sy - r, r * 2, r * 2);
  }
  ctx.globalAlpha = prevAlpha;
}

function drawStarsLegacy(ctx, bounds, timeOfDay, currentTime, decorSeed, wobbleSec) {
  var a = starFieldAlpha(timeOfDay);
  if (a <= 0.01) return;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;
  var driftPx = timeSec(currentTime) * STAR_DRIFT_PX_PER_SEC;
  var stars = layoutStarField(w, h, decorSeed);
  ctx.save();
  for (var i = 0; i < stars.length; i++) {
    var st = stars[i];
    var tw = starTwinkle(st, wobbleSec);
    paintStar(ctx, st, bounds.x + starDriftX(st, w, driftPx), bounds.y + st.y, tw.scale, a * st.alpha * tw.alpha);
  }
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds sky band above the sea
 * @param {number} timeOfDay
 * @param {number} currentTime hour-clock ms (drift)
 * @param {number} decorSeed
 * @param {{ dpr?: number, legacy?: boolean, wobbleSec?: number }} [opts]
 */
export function drawStars(ctx, bounds, timeOfDay, currentTime, decorSeed, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var wobbleSec = Number(opts.wobbleSec) || 0;
  if (opts.legacy === true) {
    drawStarsLegacy(ctx, bounds, timeOfDay, currentTime, decorSeed, wobbleSec);
    return;
  }
  var a = starFieldAlpha(timeOfDay);
  if (a <= 0.01) return;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;
  var dpr = opts.dpr != null ? Number(opts.dpr) : 1;
  var tile = ensureStarTile(w, h, decorSeed, dpr);
  if (!tile) {
    drawStarsLegacy(ctx, bounds, timeOfDay, currentTime, decorSeed, wobbleSec);
    return;
  }
  var driftPx = timeSec(currentTime) * STAR_DRIFT_PX_PER_SEC;
  blitScrollingStarTile(ctx, tile, bounds, driftPx, a);
  drawTwinklers(ctx, bounds, layoutStarField(w, h, decorSeed), driftPx, a, wobbleSec);
}
